import { LOCALES, LOCALE_NAMES, LOCALE_TAGS } from '../../src/i18n/index';
import { T, api, errorText, esc, getLocale } from '../shared/client';

/**
 * "More" screen: private dining (requests and rooms), table alerts, menus,
 * campaigns and integrations (API keys, webhooks). Everything here saves
 * immediately; none of it goes through draft and publish.
 */
interface Opts {
  restaurantId: string;
  currency: string;
  can: (perm: string) => boolean;
  toast: (msg: string, isErr?: boolean) => void;
  onClose: () => void;
  /** The section to show. Sections are chosen from the side menu. */
  tab?: string;
}
type Tab = 'private' | 'alerts' | 'menus' | 'marketing' | 'integrations';

const EVENTS = ['reservation.create', 'reservation.update', 'reservation.modify', 'reservation.move', 'reservation.seat', 'reservation.status', 'reservation.no_show', 'reservation.cancel', 'waitlist.add'];
const STATUSES = ['new', 'quoted', 'confirmed', 'declined', 'cancelled'];

export function openExtrasScreen(opts: Opts): void {
  const root = document.createElement('div');
  root.className = 'cfg-root';
  document.body.appendChild(root);
  document.body.classList.add('cfg-open');
  const base = `/v1/restaurants/${opts.restaurantId}`;

  const tabs: Tab[] = (['private', 'alerts', 'menus'] as Tab[])
    .concat(opts.can('marketing.manage') ? ['marketing'] : [])
    .concat(opts.can('integrations.manage') ? ['integrations'] : []);
  let tab: Tab = (tabs as string[]).includes(opts.tab ?? '') ? (opts.tab as Tab) : tabs[0];
  let data: Record<string, any> = {};
  let secret: { label: string; value: string } | null = null;
  let editMenu: any = null;
  let audienceCount: number | null = null;
  let calMonth = new Date().toISOString().slice(0, 7);
  const shiftMonth = (m: string, n: number) => {
    const d = new Date(`${m}-01T00:00:00Z`);
    d.setUTCMonth(d.getUTCMonth() + n);
    return d.toISOString().slice(0, 7);
  };
  const fail = (e: unknown) => opts.toast(errorText(e), true);

  const money = (minor: number | null) =>
    minor == null ? '' : new Intl.NumberFormat(LOCALE_TAGS[getLocale()], { style: 'currency', currency: opts.currency }).format(minor / 100);
  const minor = (v: string) => (v.trim() === '' ? null : Math.round(Number(v.replace(',', '.')) * 100));
  const when = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat(LOCALE_TAGS[getLocale()], { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso)) : '—';
  const day = (d: string) => new Intl.DateTimeFormat(LOCALE_TAGS[getLocale()], { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(`${d}T00:00:00Z`));
  const val = (el: Element | null, sel: string) => (el?.querySelector(sel) as HTMLInputElement | null)?.value ?? '';
  const chk = (el: Element | null, sel: string) => !!(el?.querySelector(sel) as HTMLInputElement | null)?.checked;

  async function load(which: Tab = tab) {
    try {
      if (which === 'private') {
        const [a, b, c] = await Promise.all([api<any>('GET', `${base}/private-requests`), api<any>('GET', `${base}/private-rooms`), api<any>('GET', `${base}/private-rooms/calendar?month=${calMonth}`)]);
        data.requests = a.requests;
        data.rooms = b.rooms;
        data.cal = c;
      } else if (which === 'alerts') data.alerts = (await api<any>('GET', `${base}/alerts`)).alerts;
      else if (which === 'menus') data.menus = (await api<any>('GET', `${base}/menus`)).menus;
      else if (which === 'marketing') data.campaigns = (await api<any>('GET', `${base}/campaigns`)).campaigns;
      else {
        const [k, w, d] = await Promise.all([
          api<any>('GET', `${base}/api-keys`), api<any>('GET', `${base}/webhooks`), api<any>('GET', `${base}/webhooks/deliveries`),
        ]);
        data.keys = k.keys;
        data.hooks = w.webhooks;
        data.deliveries = d.deliveries;
      }
    } catch (e) {
      fail(e);
    }
    render();
  }

  // ------------------------------------------------------------ private dining
  function privateView(): string {
    const reqs = (data.requests ?? []) as any[];
    const rooms = (data.rooms ?? []) as any[];
    const roomOpts = (sel: string | null) =>
      `<option value="">${esc(T('x.pd.reqs_none_room'))}</option>${rooms.map((r) => `<option value="${r.id}" ${sel === r.id ? 'selected' : ''}>${esc(r.name)}</option>`).join('')}`;
    const requestCard = (r: any) => `<div class="xcard" data-req="${r.id}">
        <div class="xhead"><strong>${esc(r.name)}</strong>${r.company ? `<span class="muted"> · ${esc(r.company)}</span>` : ''}
          <span class="spacer"></span><span class="chip">${esc(T(`x.pd.s.${r.status}`))}</span></div>
        <div class="small">${esc(day(r.date))} · ${esc(r.startTime)}–${esc(r.endTime)} · ${esc(T('x.pd.guests'))}: ${r.guests}${r.roomName ? ` · ${esc(r.roomName)}` : ''}</div>
        <div class="small muted">${esc(r.email)}${r.phone ? ' · ' + esc(r.phone) : ''}</div>
        ${r.occasion || r.notes ? `<div class="small">${esc([r.occasion, r.notes].filter(Boolean).join(' — '))}</div>` : ''}
        <div class="xfields">
          <label>${esc(T('x.pd.room'))}<select data-f="room">${roomOpts(r.roomId)}</select></label>
          <label>${esc(T('x.pd.status'))}<select data-f="status">${STATUSES.map((st) => `<option value="${st}" ${r.status === st ? 'selected' : ''}>${esc(T(`x.pd.s.${st}`))}</option>`).join('')}</select></label>
          <label>${esc(T('x.pd.quote'))}<input data-f="quote" type="number" min="0" step="0.01" value="${r.quoteMinor != null ? r.quoteMinor / 100 : ''}"></label>
          <label>${esc(T('x.pd.deposit'))}<input data-f="deposit" type="number" min="0" step="0.01" value="${r.depositMinor != null ? r.depositMinor / 100 : ''}"></label>
          <div class="xact"><button class="primary" data-do="req-save">${esc(T('common.save'))}</button></div>
        </div></div>`;
    const cal = data.cal as { days: { date: string; items: any[] }[] } | undefined;
    const monthName = new Intl.DateTimeFormat(LOCALE_TAGS[getLocale()], { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${calMonth}-01T00:00:00Z`));
    const calendar = `<section class="xsec"><div class="xhead"><h2 style="margin:0">${esc(T('x.cal.title'))}</h2><span class="spacer"></span>
        <button data-do="cal-prev" aria-label="${esc(T('x.cal.prev'))}">‹</button><strong style="min-width:9em;text-align:center">${esc(monthName)}</strong><button data-do="cal-next" aria-label="${esc(T('x.cal.next'))}">›</button></div>
        ${cal && cal.days.length ? cal.days.map((d) => `<div class="xcard"><strong>${esc(day(d.date))}</strong>${d.items.map((r) => `<div class="xline">
            <span class="chip xs-${r.status}">${esc(T(`x.pd.s.${r.status}`))}</span> <strong>${esc(r.roomName ?? T('x.cal.no_room'))}</strong>
            <span>${esc(r.startTime)}–${esc(r.endTime)}</span><span class="muted">${esc(r.name)}${r.company ? ' · ' + esc(r.company) : ''} · ${r.guests}</span></div>`).join('')}</div>`).join('')
          : `<p class="muted">${esc(T('x.cal.none'))}</p>`}</section>`;
    return `${calendar}<section class="xsec"><h2>${esc(T('x.pd.requests'))}</h2>
        <p class="muted small">${esc(T('x.pd.mail_hint'))}</p>
        ${reqs.length ? reqs.map(requestCard).join('') : `<p class="muted">${esc(T('x.pd.none'))}</p>`}</section>
      <section class="xsec"><h2>${esc(T('x.pd.rooms'))}</h2>
        <p class="muted small">${esc(T('x.pd.rooms_hint'))}</p>
        ${rooms.map((r) => roomCard(r)).join('')}${roomCard(null)}</section>`;
  }
  function roomCard(r: any | null): string {
    return `<div class="xcard" ${r ? `data-room="${r.id}"` : 'id="room-new"'}>
      <div class="xfields">
        <label class="wide">${esc(T('x.pd.room_name'))}<input data-f="name" maxlength="80" value="${esc(r?.name ?? '')}" placeholder="${esc(T('x.pd.room_new'))}"></label>
        <label>${esc(T('x.pd.min'))}<input data-f="min" type="number" min="1" value="${r?.minGuests ?? 8}"></label>
        <label>${esc(T('x.pd.max'))}<input data-f="max" type="number" min="1" value="${r?.maxGuests ?? 20}"></label>
      </div>
      <div class="xfields">${LOCALES.map((l) => `<label class="wide">${esc(LOCALE_NAMES[l])}<input data-d="${l}" maxlength="600" value="${esc(r?.description?.[l] ?? '')}"></label>`).join('')}</div>
      <div class="xfields"><label class="cfg-check"><input data-f="active" type="checkbox" ${r?.active === false ? '' : 'checked'}> ${esc(T('x.active'))}</label>
        <div class="xact">${r ? `<button class="primary" data-do="room-save">${esc(T('x.pd.save_room'))}</button> <button class="danger" data-do="room-del">${esc(T('g.delete'))}</button>`
          : `<button class="primary" data-do="room-add">${esc(T('g.add'))}</button>`}</div></div></div>`;
  }
  const roomBody = (tr: Element | null) => ({
    name: val(tr, '[data-f=name]').trim(),
    description: Object.fromEntries(LOCALES.map((l) => [l, val(tr, `[data-d=${l}]`).trim()]).filter(([, v]) => v)),
    minGuests: Number(val(tr, '[data-f=min]')) || 1,
    maxGuests: Number(val(tr, '[data-f=max]')) || 1,
    active: chk(tr, '[data-f=active]'),
  });

  // ------------------------------------------------------------ alerts
  function alertsView(): string {
    const list = (data.alerts ?? []) as any[];
    return `<p class="muted small">${esc(T('x.al.hint'))}</p>
      <table class="cfg-table"><thead><tr><th>${esc(T('x.pd.when'))}</th><th>${esc(T('x.pd.who'))}</th><th class="num">${esc(T('x.pd.guests'))}</th><th>${esc(T('x.pd.status'))}</th><th></th></tr></thead><tbody>
      ${list.length ? list.map((a) => `<tr><td class="small">${esc(day(a.date))}<br>${esc(a.fromTime)}–${esc(a.toTime)}</td>
        <td>${esc(a.name)}${a.priority ? ` <span class="chip">${esc(T('x.al.priority'))}</span>` : ''}<br><span class="small muted">${esc(a.email)}</span></td>
        <td class="num">${a.partySize}</td><td>${esc(T(`x.al.s.${a.status}`))}${a.notifiedAt ? `<br><span class="small muted">${esc(when(a.notifiedAt))}</span>` : ''}</td>
        <td>${a.status === 'waiting' ? `<button data-do="alert-del" data-id="${a.id}">${esc(T('x.cancel'))}</button>` : ''}</td></tr>`).join('')
        : `<tr><td colspan="5" class="muted">${esc(T('x.al.none'))}</td></tr>`}</tbody></table>`;
  }

  // ------------------------------------------------------------ menus
  const menuText = (m: any, l: string): string =>
    (m.sections ?? [])
      .map((s: any) => `# ${s.titles?.[l] ?? ''}\n${(s.items ?? [])
        .map((i: any) => [i.names?.[l] ?? '', i.priceMinor != null ? String(i.priceMinor / 100) : '', i.descriptions?.[l] ?? '', (i.labels ?? []).join(',')].join(' | '))
        .join('\n')}`)
      .join('\n\n');
  /** One text per language, same layout: sections start with "#", items are "name | price | description | labels". */
  function parseMenu(texts: Record<string, string>): any[] {
    const parsed = Object.fromEntries(
      Object.entries(texts).map(([l, tx]) => {
        const secs: { title: string; items: string[][] }[] = [];
        for (const raw of tx.split('\n')) {
          const line = raw.trim();
          if (!line) continue;
          if (line.startsWith('#')) secs.push({ title: line.replace(/^#+\s*/, ''), items: [] });
          else {
            if (!secs.length) secs.push({ title: '', items: [] });
            secs[secs.length - 1].items.push(line.split('|').map((p) => p.trim()));
          }
        }
        return [l, secs];
      }),
    ) as Record<string, { title: string; items: string[][] }[]>;
    const lead = LOCALES.find((l) => parsed[l]?.length) ?? LOCALES[0];
    return (parsed[lead] ?? []).map((s, si) => ({
      titles: Object.fromEntries(LOCALES.map((l) => [l, parsed[l]?.[si]?.title ?? '']).filter(([, v]) => v)),
      items: s.items.map((it, ii) => ({
        names: Object.fromEntries(LOCALES.map((l) => [l, parsed[l]?.[si]?.items[ii]?.[0] ?? '']).filter(([, v]) => v)),
        descriptions: Object.fromEntries(LOCALES.map((l) => [l, parsed[l]?.[si]?.items[ii]?.[2] ?? '']).filter(([, v]) => v)),
        priceMinor: it[1] ? Math.round(Number(it[1].replace(',', '.')) * 100) : null,
        labels: (it[3] ?? '').split(',').map((x) => x.trim()).filter(Boolean).slice(0, 6),
      })),
    }));
  }
  function menusView(): string {
    const list = (data.menus ?? []) as any[];
    if (editMenu) {
      const m = editMenu;
      return `<div class="cfg-card" id="menu-form"><h3 style="margin-top:0">${esc(m.id ? T('x.mn.edit') : T('x.mn.new'))}</h3>
        <p class="muted small">${esc(T('x.mn.format'))}</p>
        ${LOCALES.map((l) => `<fieldset><legend>${esc(LOCALE_NAMES[l])}</legend>
          <label>${esc(T('x.mn.title'))} <input data-t="${l}" maxlength="80" value="${esc(m.titles?.[l] ?? '')}"></label>
          <label>${esc(T('x.mn.body'))} <textarea data-m="${l}" rows="9" style="width:100%;font-family:ui-monospace,monospace">${esc(menuText(m, l))}</textarea></label></fieldset>`).join('')}
        <label class="cfg-check"><input type="checkbox" id="menu-active" ${m.active === false ? '' : 'checked'}> ${esc(T('x.mn.active'))}</label>
        <div class="cfg-row"><button class="primary" data-do="menu-save">${esc(T('common.save'))}</button><button data-do="menu-cancel">${esc(T('x.cancel'))}</button></div></div>`;
    }
    return `<div class="cfg-row"><button class="primary" data-do="menu-new">${esc(T('x.mn.new'))}</button></div>
      ${list.length ? list.map((m, i) => `<div class="cfg-card"><div class="cfg-row"><strong style="flex:1">${esc(m.titles[getLocale()] || m.titles.en || Object.values(m.titles)[0] || '')}</strong>
        <span class="muted small">${m.active ? esc(T('x.mn.shown')) : esc(T('x.mn.hidden'))} · ${(m.sections as any[]).reduce((n, s) => n + s.items.length, 0)} ${esc(T('x.mn.items'))}</span>
        <button data-do="menu-edit" data-i="${i}">${esc(T('x.edit'))}</button><button class="danger" data-do="menu-del" data-i="${i}">${esc(T('g.delete'))}</button></div></div>`).join('')
        : `<p class="muted">${esc(T('x.mn.none'))}</p>`}`;
  }

  // ------------------------------------------------------------ campaigns
  function marketingView(): string {
    const list = (data.campaigns ?? []) as any[];
    return `<div class="cfg-card" id="camp-form"><h3 style="margin-top:0">${esc(T('x.mk.new'))}</h3>
      <p class="muted small">${esc(T('x.mk.hint'))}</p>
      <label>${esc(T('x.mk.subject'))} <input id="c-subject" maxlength="150"></label>
      <label>${esc(T('x.mk.body'))} <textarea id="c-body" rows="6" maxlength="5000" style="width:100%"></textarea></label>
      <div class="cfg-row">
        <label>${esc(T('x.mk.tag'))} <input id="c-tag" maxlength="30" placeholder="${esc(T('x.mk.any'))}"></label>
        <label>${esc(T('x.mk.min_visits'))} <input id="c-min" type="number" min="0" style="width:90px"></label>
        <label>${esc(T('x.mk.not_seen'))} <input id="c-days" type="number" min="1" style="width:90px"></label>
      </div>
      <div class="cfg-row"><button data-do="camp-preview">${esc(T('x.mk.preview'))}</button>
        <span id="c-count" class="muted">${audienceCount == null ? '' : esc(T('x.mk.count', { n: audienceCount ?? 0 }))}</span>
        <span class="spacer"></span><button class="primary" data-do="camp-send">${esc(T('x.mk.send'))}</button></div></div>
      <h3>${esc(T('x.mk.sent'))}</h3>
      <table class="cfg-table"><thead><tr><th>${esc(T('x.pd.when'))}</th><th>${esc(T('x.mk.subject'))}</th><th class="num">${esc(T('x.mk.recipients'))}</th><th class="num">${esc(T('x.mk.delivered'))}</th></tr></thead><tbody>
      ${list.length ? list.map((c) => `<tr><td class="small">${esc(when(c.createdAt))}</td><td>${esc(c.subject)}</td><td class="num">${c.recipients}</td><td class="num">${c.sent}</td></tr>`).join('')
        : `<tr><td colspan="4" class="muted">${esc(T('x.mk.none'))}</td></tr>`}</tbody></table>`;
  }
  const audience = () => {
    const n = (id: string) => {
      const v = (root.querySelector(id) as HTMLInputElement).value.trim();
      return v === '' ? null : Number(v);
    };
    return {
      tag: (root.querySelector('#c-tag') as HTMLInputElement).value.trim() || null,
      minVisits: n('#c-min'),
      notSeenForDays: n('#c-days'),
    };
  };

  // ------------------------------------------------------------ integrations
  function integrationsView(): string {
    const keys = (data.keys ?? []) as any[];
    const hooks = (data.hooks ?? []) as any[];
    const dels = (data.deliveries ?? []) as any[];
    return `${secret ? `<div class="notice" role="status"><strong>${esc(secret.label)}</strong><br><code style="user-select:all;word-break:break-all">${esc(secret.value)}</code>
        <p class="small" style="margin:6px 0 0">${esc(T('x.in.once'))}</p></div>` : ''}
      <h3>${esc(T('x.in.keys'))}</h3><p class="muted small">${esc(T('x.in.keys_hint'))}</p>
      <table class="cfg-table"><thead><tr><th>${esc(T('x.in.name'))}</th><th>${esc(T('x.in.scope'))}</th><th>${esc(T('x.in.last_used'))}</th><th></th></tr></thead><tbody>
      ${keys.map((k) => `<tr><td>${esc(k.name)} <span class="muted small">${esc(k.prefix)}…</span></td><td>${esc(T(`x.in.scope.${k.scope}`))}</td>
        <td class="small">${esc(k.revoked ? T('x.in.revoked') : when(k.lastUsedAt))}</td>
        <td>${k.revoked ? '' : `<button class="danger" data-do="key-del" data-id="${k.id}">${esc(T('x.in.revoke'))}</button>`}</td></tr>`).join('')}
      <tr id="key-new"><td><input data-f="name" maxlength="60" placeholder="${esc(T('x.in.key_new'))}"></td>
        <td><select data-f="scope" aria-label="${esc(T('x.in.scope'))}"><option value="read">${esc(T('x.in.scope.read'))}</option><option value="write">${esc(T('x.in.scope.write'))}</option></select></td><td></td>
        <td><button class="primary" data-do="key-add">${esc(T('g.add'))}</button></td></tr></tbody></table>
      <h3>${esc(T('x.in.hooks'))}</h3><p class="muted small">${esc(T('x.in.hooks_hint'))}</p>
      <table class="cfg-table"><thead><tr><th>${esc(T('x.in.url'))}</th><th>${esc(T('x.in.events'))}</th><th></th></tr></thead><tbody>
      ${hooks.map((h) => `<tr><td style="word-break:break-all">${esc(h.url)}${h.failed ? ` <span class="over small">${esc(T('x.in.failed', { n: h.failed }))}</span>` : ''}</td>
        <td class="small">${esc((h.events as string[]).length === EVENTS.length ? T('x.in.all_events') : (h.events as string[]).join(', '))}</td>
        <td><button data-do="hook-test" data-id="${h.id}">${esc(T('x.in.test'))}</button> <button class="danger" data-do="hook-del" data-id="${h.id}">${esc(T('g.delete'))}</button></td></tr>`).join('')}
      <tr id="hook-new"><td><input data-f="url" maxlength="500" placeholder="https://" style="width:100%"></td>
        <td class="small">${esc(T('x.in.all_events'))}</td><td><button class="primary" data-do="hook-add">${esc(T('g.add'))}</button></td></tr></tbody></table>
      <h3>${esc(T('x.in.pos'))}</h3><p class="muted small">${esc(T('x.in.pos_hint'))}</p>
      <div class="xcard"><strong>POST ${esc(location.origin)}${esc(base)}/sales</strong>
        <span class="small muted">${esc(T('x.in.pos_example'))}</span>
        <pre class="small" style="margin:0;white-space:pre-wrap;user-select:all">{ "sales": [ { "externalId": "check-10234", "source": "mypos", "reservationCode": "ABCD2345",
  "covers": 4, "grossMinor": 18650, "netMinor": 15672, "tipMinor": 1500, "closedAt": "2026-11-20T21:14:00Z" } ] }</pre></div>
      <h3>${esc(T('x.in.log'))}</h3>
      <table class="cfg-table"><tbody>${dels.length ? dels.map((d) => `<tr><td class="small">${esc(when(d.at))}</td><td>${esc(d.event)}</td>
        <td class="small" style="word-break:break-all">${esc(d.url)}</td><td>${esc(T(`x.in.d.${d.status}`))}${d.error ? ` <span class="muted small">${esc(d.error)}</span>` : ''}</td></tr>`).join('')
        : `<tr><td class="muted">${esc(T('x.in.log_none'))}</td></tr>`}</tbody></table>`;
  }

  // ------------------------------------------------------------ shell
  function render() {
    const body = { private: privateView, alerts: alertsView, menus: menusView, marketing: marketingView, integrations: integrationsView }[tab]();
    const keep = {
      subject: (root.querySelector('#c-subject') as HTMLInputElement | null)?.value,
    };
    root.innerHTML = `<div class="cfg-bar"><strong>${esc(T(`x.tab.${tab}`))}</strong>
        <span class="spacer"></span><button data-do="close">${esc(T('common.close'))}</button></div>
      <div class="cfg-body cfg-solo"><main class="cfg-main xpage">${body}</main></div>`;
    void keep;
  }

  root.addEventListener('click', async (ev) => {
    const b = (ev.target as HTMLElement).closest('[data-do]') as HTMLElement | null;
    if (!b) return;
    const tr = b.closest('[data-req],[data-room],#room-new,#key-new,#hook-new,tr') as HTMLElement | null;
    const id = b.dataset.id ?? tr?.dataset.req ?? tr?.dataset.room;
    try {
      switch (b.dataset.do) {
        case 'close':
          root.remove();
          document.body.classList.remove('cfg-open');
          opts.onClose();
          return;
        case 'tab':
          tab = b.dataset.t as Tab;
          secret = null;
          editMenu = null;
          render();
          return void (await load(tab));
        case 'req-save': {
          const body = {
            status: val(tr, '[data-f=status]'),
            roomId: val(tr, '[data-f=room]') || null,
            quoteMinor: minor(val(tr, '[data-f=quote]')),
            depositMinor: minor(val(tr, '[data-f=deposit]')),
          };
          data.requests = (await api<any>('PATCH', `${base}/private-requests/${id}`, body)).requests;
          opts.toast(T('x.saved'));
          return render();
        }
        case 'cal-prev':
        case 'cal-next':
          calMonth = shiftMonth(calMonth, b.dataset.do === 'cal-prev' ? -1 : 1);
          return void (await load('private'));
        case 'room-add':
          data.rooms = (await api<any>('POST', `${base}/private-rooms`, roomBody(tr))).rooms;
          return render();
        case 'room-save':
          data.rooms = (await api<any>('PUT', `${base}/private-rooms/${id}`, roomBody(tr))).rooms;
          opts.toast(T('x.saved'));
          return render();
        case 'room-del':
          if (!confirm(T('x.pd.room_del'))) return;
          data.rooms = (await api<any>('DELETE', `${base}/private-rooms/${id}`)).rooms;
          return render();
        case 'alert-del':
          data.alerts = (await api<any>('DELETE', `${base}/alerts/${id}`)).alerts;
          return render();
        case 'menu-new':
          editMenu = { id: null, titles: {}, sections: [], active: true };
          return render();
        case 'menu-edit':
          editMenu = structuredClone(data.menus[Number(b.dataset.i)]);
          return render();
        case 'menu-cancel':
          editMenu = null;
          return render();
        case 'menu-del':
          if (!confirm(T('x.mn.del'))) return;
          data.menus = (await api<any>('DELETE', `${base}/menus/${data.menus[Number(b.dataset.i)].id}`)).menus;
          return render();
        case 'menu-save': {
          const f = root.querySelector('#menu-form')!;
          const titles = Object.fromEntries(LOCALES.map((l) => [l, val(f, `[data-t=${l}]`).trim()]).filter(([, v]) => v));
          const texts = Object.fromEntries(LOCALES.map((l) => [l, val(f, `[data-m=${l}]`)]));
          const body = { titles, sections: parseMenu(texts), active: chk(f, '#menu-active') };
          data.menus = editMenu.id
            ? (await api<any>('PUT', `${base}/menus/${editMenu.id}`, body)).menus
            : (await api<any>('POST', `${base}/menus`, body)).menus;
          editMenu = null;
          opts.toast(T('x.saved'));
          return render();
        }
        case 'camp-preview':
          audienceCount = (await api<any>('POST', `${base}/campaigns/preview`, audience())).recipients;
          (root.querySelector('#c-count') as HTMLElement).textContent = T('x.mk.count', { n: audienceCount ?? 0 });
          return;
        case 'camp-send': {
          const subject = (root.querySelector('#c-subject') as HTMLInputElement).value.trim();
          const text = (root.querySelector('#c-body') as HTMLTextAreaElement).value.trim();
          if (!subject || !text) return opts.toast(T('x.mk.fill'), true);
          const n = (await api<any>('POST', `${base}/campaigns/preview`, audience())).recipients;
          if (!confirm(T('x.mk.confirm', { n }))) return;
          const r = await api<any>('POST', `${base}/campaigns`, { subject, body: text, audience: audience() });
          data.campaigns = r.campaigns;
          audienceCount = null;
          opts.toast(T('x.mk.done', { n: r.sent }));
          return render();
        }
        case 'key-add': {
          const name = val(tr, '[data-f=name]').trim();
          if (!name) return;
          const r = await api<any>('POST', `${base}/api-keys`, { name, scope: val(tr, '[data-f=scope]') });
          data.keys = r.keys;
          secret = { label: T('x.in.key_made', { name }), value: r.token };
          return render();
        }
        case 'key-del':
          if (!confirm(T('x.in.revoke_confirm'))) return;
          data.keys = (await api<any>('DELETE', `${base}/api-keys/${id}`)).keys;
          return render();
        case 'hook-add': {
          const url = val(tr, '[data-f=url]').trim();
          if (!url) return;
          const r = await api<any>('POST', `${base}/webhooks`, { url });
          data.hooks = r.webhooks;
          secret = { label: T('x.in.hook_made'), value: r.secret };
          return void (await load('integrations'));
        }
        case 'hook-del':
          if (!confirm(T('x.in.hook_del'))) return;
          data.hooks = (await api<any>('DELETE', `${base}/webhooks/${id}`)).webhooks;
          return render();
        case 'hook-test':
          await api('POST', `${base}/webhooks/${id}/test`, {});
          return void (await load('integrations'));
      }
    } catch (e) {
      fail(e);
    }
  });
  root.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') (root.querySelector('[data-do="close"]') as HTMLElement | null)?.click();
  });

  render();
  void load();
}
